import { useEffect, useState } from 'react';
import { fetchModProgress, type ModProgress } from '../api/servers';
import { ca } from '../i18n/ca';

const POLL_MS = 3000;

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
}

/**
 * The panel's download of the mods a server request was refused for, advancing
 * until every one is installed. It asks the platform every few seconds and
 * stops when all are ready; when the platform cannot answer it says nothing
 * more than the plain "they are downloading" sentence above it already did.
 */
export function ModDownloads({ ids }: { ids: number[] }) {
  const s = ca.serversPage;
  const [progress, setProgress] = useState<ModProgress[] | null>(null);
  const key = ids.join(',');

  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const wanted = key.split(',').map(Number);
    const poll = () => {
      fetchModProgress(wanted)
        .then((rows) => {
          if (!alive) return;
          setProgress(rows);
          if (!rows.every((r) => r.state === 'ready')) timer = setTimeout(poll, POLL_MS);
        })
        .catch(() => {
          if (alive) timer = setTimeout(poll, POLL_MS);
        });
    };
    poll();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [key]);

  if (!progress || progress.length === 0) return null;
  const allReady = progress.every((r) => r.state === 'ready');
  return (
    <section className="mod-downloads" aria-label={s.downloadsTitle}>
      <h3 className="small">{s.downloadsTitle}</h3>
      <ul>
        {progress.map((r) => (
          <li key={r.id}>
            <span>{r.name}</span>{' '}
            <span className="muted small">
              {s.downloadState[r.state] ?? r.state}
              {r.state === 'downloading' && r.percent !== null ? ` · ${r.percent} %` : ''}
              {r.state === 'downloading' && r.bytesFinished !== null && r.bytesTotal !== null
                ? ` · ${megabytes(r.bytesFinished)} / ${megabytes(r.bytesTotal)}`
                : ''}
            </span>{' '}
            {r.state === 'downloading' && r.percent !== null && (
              <progress max={100} value={r.percent} aria-label={r.name} />
            )}
          </li>
        ))}
      </ul>
      {allReady && <p role="status">{s.downloadsReady}</p>}
    </section>
  );
}
